/**
 * `<carousel-ring>` — a draggable 3D card carousel custom element.
 *
 * Drag (mouse/touch/pen) spins the ring; on release it coasts to a stop
 * (inertia). State is a single `angle` whose setter writes the ring's
 * transform directly. Styling is addressed by classes, elements are found
 * via `data-role` only.
 *
 * CKC perf (2 Oct 2026):
 *  - The setter used to write a `--angle` CSS variable. A variable change makes
 *    the browser re-work the styles of every card in the ring, every frame, while
 *    it auto-spins. Writing the transform itself does the same rotation with far
 *    less work per frame. The CSS rule `rotateY(var(--angle))` is still there as a
 *    fallback; the inline transform simply takes over.
 *  - The momentum loop used to run 60 times a second forever, even with the ring
 *    standing still or off screen. It now runs only while the ring is coasting.
 *
 * Spinning only begins after the pointer travels past `DRAG_THRESHOLD` pixels,
 * so a plain click/tap on a card never nudges the ring. The pointer is captured
 * only once that real drag starts, leaving clicks on inner buttons intact.
 *
 * Expected DOM:
 * ```html
 * <carousel-ring class="carousel">
 *   <div data-role="carousel-container" style="--cards-count: N">
 *     <div data-role="carousel-item" style="--card-index: 0"> … </div>
 *   </div>
 * </carousel-ring>
 * ```
 *
 * Config (`this.config`):
 *  `SENSITIVITY`    — degrees per dragged pixel;
 *  `FRICTION`       — per-frame inertia decay (0–1);
 *  `DRAG_THRESHOLD` — pixels of travel before a drag starts (click deadzone).
 *
 * @element carousel-ring
 */

if (customElements && !customElements.get('carousel-ring')) {
  class CarouselRing extends HTMLElement {
    #angle = 0;

    constructor() {
      super();

      this.config = {
        SENSITIVITY: 0.20,
        FRICTION: 0.75,
        DRAG_THRESHOLD: 6
      };

      this.velocity = 0;
      this.pressed = false;
      this.dragging = false;
      this.startX = 0;
      this.lastX = 0;
      this.coasting = false;
    }

    connectedCallback() {
      this.ring = this.querySelector('[data-role="carousel-container"]');
      if (this.ring) this.ring.style.willChange = 'transform';

      this.bindEvents();
      this.angle = this.#angle;
    }

    get angle() {
      return this.#angle;
    }

    set angle(value) {
      this.#angle = value;
      if (this.ring) this.ring.style.transform = 'rotateY(' + value + 'deg)';
    }

    onPointerDown = (e) => {
      this.pressed = true;
      this.dragging = false;
      this.velocity = 0;
      this.startX = e.clientX;
      this.lastX = e.clientX;
    }

    onPointerMove = (e) => {
      if (!this.pressed) return;

      const dx = e.clientX - this.lastX;
      this.lastX = e.clientX;

      if (!this.dragging) {
        if (Math.abs(e.clientX - this.startX) < this.config.DRAG_THRESHOLD) return;

        this.dragging = true;
        this.classList.add('carousel--grabbing');
        this.setPointerCapture(e.pointerId);
      }

      this.velocity = dx * this.config.SENSITIVITY * -1;
      this.angle += this.velocity;
    }

    onPointerUp = (e) => {
      this.pressed = false;
      this.dragging = false;
      this.classList.remove('carousel--grabbing');
      if (this.hasPointerCapture(e.pointerId)) {
        this.releasePointerCapture(e.pointerId);
      }
      this.coast();
    }

    onDragStart = (e) => {
      e.preventDefault();
    }

    /* start the momentum loop if there is momentum to spend */
    coast() {
      if (this.coasting || Math.abs(this.velocity) <= 0.02) return;
      this.coasting = true;
      requestAnimationFrame(this.tick);
    }

    tick = () => {
      if (!this.dragging && Math.abs(this.velocity) > 0.02) {
        this.angle += this.velocity;
        this.velocity *= this.config.FRICTION;
        requestAnimationFrame(this.tick);
        return;
      }
      this.coasting = false;
    }

    bindEvents() {
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);
      this.addEventListener('dragstart', this.onDragStart);
    }
  }

  customElements.define('carousel-ring', CarouselRing);
}
